'use client'

import { Suspense, useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'next/navigation'
import Link from 'next/link'
import Image from 'next/image'
import { SocialLoginButtons } from '@/components/auth/SocialLoginButtons'
import { Loader2 } from 'lucide-react'
import { trackEvent, runWhenPageVisible } from '@/lib/analytics'
import { BRAND_ASSET_PATHS } from '@/lib/brand-assets'

const OAUTH_PENDING_REDIRECT_KEY = 'oauth_pending_redirect'
const OAUTH_PENDING_REDIRECT_TTL_MS = 15 * 60 * 1000
const OAUTH_ERROR_CLASSES = new Set(['provider_error', 'no_code', 'exchange_failed'])
const LOGIN_REDIRECT_BASE = 'https://whatgrassisthis.invalid'
const LOGIN_ERROR_MESSAGES: Record<string, string> = {
  authentication_required: 'Please sign in to continue.',
  session_expired: 'Your session expired. Please sign in again.',
  account_link_failed: 'We could not link that account. Please try again.',
}

function normalizeLoginRedirect(value: string | null): string {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) {
    return '/dashboard'
  }

  try {
    const destination = new URL(value, LOGIN_REDIRECT_BASE)
    if (destination.origin !== LOGIN_REDIRECT_BASE) return '/dashboard'
    return `${destination.pathname}${destination.search}${destination.hash}`
  } catch {
    return '/dashboard'
  }
}

function loginErrorMessage(errorCode: string | null): string | null {
  if (!errorCode) return null
  return LOGIN_ERROR_MESSAGES[errorCode] ?? 'We could not sign you in. Please try again.'
}

function recoveryDestinationLabel(destination: string): string {
  try {
    const pathname = new URL(destination, LOGIN_REDIRECT_BASE).pathname
    if (pathname.startsWith('/dashboard/reminders')) return 'your reminders'
    if (pathname.startsWith('/dashboard/history')) return 'your analysis history'
    if (pathname.startsWith('/dashboard/billing')) return 'your billing page'
    if (pathname.startsWith('/dashboard/settings')) return 'your account settings'
    if (pathname.startsWith('/dashboard')) return 'your dashboard'
    if (pathname.startsWith('/timeline')) return 'your lawn plan'
    if (pathname.startsWith('/diagnose')) return 'your lawn diagnosis'
    if (pathname.startsWith('/weed-identifier')) return 'your weed ID'
    if (pathname === '/') return 'your grass ID'
  } catch {
    // A malformed destination is normalized before this helper is called.
  }
  return 'where you left off'
}

function normalizeOAuthErrorClass(value: string | null): string | null {
  if (!value) return null
  return OAUTH_ERROR_CLASSES.has(value) ? value : 'exchange_failed'
}

function readFreshPendingOAuthRedirect(): string | null {
  if (typeof window === 'undefined') return null

  try {
    const raw = window.localStorage.getItem(OAUTH_PENDING_REDIRECT_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as { path?: unknown; savedAt?: unknown }
    if (typeof parsed.path !== 'string' || typeof parsed.savedAt !== 'number') return null
    if (Date.now() - parsed.savedAt > OAUTH_PENDING_REDIRECT_TTL_MS) {
      window.localStorage.removeItem(OAUTH_PENDING_REDIRECT_KEY)
      return null
    }

    const destination = new URL(parsed.path, window.location.origin)
    if (destination.origin !== window.location.origin) return null
    return `${destination.pathname}${destination.search}${destination.hash}`
  } catch {
    return null
  }
}

function LoginForm() {
  const searchParams = useSearchParams()
  const redirectTo = normalizeLoginRedirect(searchParams.get('redirect'))
  const errorFromUrl = searchParams.get('error')
  const oauthErrorClass = normalizeOAuthErrorClass(searchParams.get('oauth_error'))
  // /auth/login serves both jobs — OAuth has no separate signup — so entry
  // points that mean "create an account" say so with ?intent=signup and get
  // the create-account framing. Anything else falls back to the neutral copy.
  const isSignupIntent = searchParams.get('intent') === 'signup'
  const [error, setError] = useState<string | null>(null)
  const [retryRedirectTo, setRetryRedirectTo] = useState(redirectTo)
  const reportedOauthErrorRef = useRef<string | null>(null)

  useEffect(() => {
    if (oauthErrorClass) {
      setError(null)
      return
    }
    setError(loginErrorMessage(errorFromUrl))
  }, [errorFromUrl, oauthErrorClass])

  // Which framing the arrival saw, so signup-intent arrivals can be measured
  // against neutral ones. Held behind runWhenPageVisible: ~27% of sessions
  // never surface a $pageview, and a mount-time "viewed" event fired in a
  // prerendered/background tab would inflate the denominator.
  const viewReportedRef = useRef(false)
  useEffect(() => {
    if (viewReportedRef.current) return
    viewReportedRef.current = true
    return runWhenPageVisible(() => {
      trackEvent('login_page_viewed', {
        intent: isSignupIntent ? 'signup' : 'signin',
        has_redirect: Boolean(redirectTo),
      })
    })
  }, [isSignupIntent, redirectTo])

  useEffect(() => {
    if (!oauthErrorClass) return

    setRetryRedirectTo(readFreshPendingOAuthRedirect() ?? redirectTo)
    if (reportedOauthErrorRef.current === oauthErrorClass) return
    reportedOauthErrorRef.current = oauthErrorClass
    trackEvent('oauth_completion_failed', { error_class: oauthErrorClass })
    trackEvent('oauth_retry_shown', { error_class: oauthErrorClass })
  }, [oauthErrorClass, redirectTo])

  return (
    <div className="w-full max-w-5xl grid md:grid-cols-[1.05fr_1fr] gap-12 md:gap-20 items-center">
      {/* Left — pitch */}
      <div className="hidden md:block">
        <div className="inline-flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full bg-green-50 border border-green-100 text-xs font-semibold text-green-700 mb-6">
          <span className="w-1.5 h-1.5 rounded-full bg-green-600" />
          Free · no credit card
        </div>

        <h2 className="text-4xl md:text-[44px] font-bold text-gray-900 leading-[1.05] tracking-tight mb-4 text-balance">
          Pick up where<br />
          your{' '}
          <span className="text-green-600 border-b-[3px] border-green-600 pb-0.5">
            lawn left off.
          </span>
        </h2>

        <p className="text-base text-gray-700 leading-relaxed mb-9 max-w-[460px]">
          Sign in to keep your saved lawn, care plan, and seasonal alerts tied
          to your yard across every device. Premium includes your full analysis history.
        </p>

        <div className="flex flex-col gap-5">
          <Benefit
            icon={<IconSavedYards />}
            title="Your lawn, saved"
            sub="Keep your lawn profile and care plan together under one account."
          />
          <Benefit
            icon={<IconCarePlan />}
            title="Care plan that follows the season"
            sub="Watering, mowing, and fertilizing, tuned to your grass and your ZIP."
          />
          <Benefit
            icon={<IconBell />}
            title="A heads-up when it matters"
            sub="One email when your pre-emergent or aeration window opens. No newsletter."
          />
        </div>

        <div className="mt-9 pt-6 border-t border-gray-200">
          <div className="text-[13px] text-gray-500 leading-snug">
            Free for everyone. Sign-in saves your lawn and care plan.{' '}
            <strong className="text-gray-900 font-semibold">Full grass ID history is a Premium feature</strong>.
          </div>
        </div>
      </div>

      {/* Right — sign-in card */}
      <div className="relative min-w-0">
        {/* soft glow */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-[32px]"
          style={{
            background:
              'radial-gradient(60% 50% at 50% 30%, rgba(22,163,74,0.10), transparent 70%)',
          }}
        />

        <div className="relative bg-white border border-gray-200 rounded-2xl px-9 pt-9 pb-7 shadow-[0_12px_40px_rgba(15,29,20,0.06),0_1px_2px_rgba(0,0,0,0.04)]">
          <div className="mb-6 text-center">
            <h1 className="text-[22px] font-bold text-gray-900 tracking-tight">
              {isSignupIntent ? 'Create your account' : 'Sign in or create account'}
            </h1>
            <div className="text-sm text-gray-500 mt-1">
              {isSignupIntent
                ? "We don't do passwords. Pick one and your account is ready."
                : "We don't do passwords. Pick one and you're in."}
            </div>
          </div>

          {oauthErrorClass ? (
            <div
              role="alert"
              aria-live="assertive"
              aria-atomic="true"
              className="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-4"
            >
              <div className="text-base font-semibold text-amber-950">
                Sign-in didn&apos;t finish
              </div>
              <p className="mt-1 mb-4 text-sm leading-relaxed text-amber-900">
                Nothing was lost. Try again and we will bring you back to{' '}
                {recoveryDestinationLabel(retryRedirectTo)}.
              </p>
              <SocialLoginButtons
                placement="login_page_retry"
                redirectTo={retryRedirectTo}
                onBeforeRedirect={() => ({ retry: true })}
              />
            </div>
          ) : error && (
            <div role="alert" className="p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {!oauthErrorClass && (
            <SocialLoginButtons
              placement="login_page"
              redirectTo={redirectTo}
              onError={(err) => setError(err.message)}
            />
          )}

          {/* Brand lockup at the card foot: the left pitch column is hidden on
              mobile, so without this the card has no indication of whose
              sign-in it is. Kept below the actions so it doesn't compete with
              the primary ask. */}
          <div className="mt-5 pt-4 border-t border-gray-200 text-center">
            <Link
              href="/"
              className="inline-flex items-center gap-1.5 no-underline opacity-80 hover:opacity-100"
            >
              <Image
                src={BRAND_ASSET_PATHS.iconDefault}
                alt="What Grass Is This? Logo"
                width={24}
                height={24}
                className="h-5 w-5"
              />
              <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
                What Grass Is This?
              </span>
            </Link>
          </div>

          {process.env.NODE_ENV !== 'production' && (
            <div className="mt-3 text-center">
              <button
                type="button"
                onClick={async () => {
                  const res = await fetch('/api/dev/login', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ redirectTo }),
                  })
                  const json = await res.json()
                  if (!res.ok) {
                    setError(json.error ?? 'Dev login failed')
                    return
                  }
                  window.location.href = json.redirectTo ?? '/'
                }}
                className="text-[12px] text-amber-700 font-medium hover:text-amber-900 underline decoration-dotted"
              >
                Dev sign-in (local only)
              </button>
            </div>
          )}
        </div>

        <div className="text-center mt-4 text-xs text-gray-600 leading-relaxed">
          By continuing you agree to our{' '}
          <Link href="/terms" className="text-gray-700 underline">Terms</Link>
          {' '}&{' '}
          <Link href="/privacy" className="text-gray-700 underline">Privacy Policy</Link>.
        </div>
      </div>
    </div>
  )
}

function Benefit({
  icon,
  title,
  sub,
}: {
  icon: React.ReactNode
  title: string
  sub: string
}) {
  return (
    <div className="flex gap-3.5 items-start">
      <div className="w-10 h-10 rounded-xl bg-green-50 border border-green-100 flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div className="pt-0.5">
        <div className="text-[15px] font-semibold text-gray-900 mb-0.5 tracking-tight">
          {title}
        </div>
        <div className="text-sm text-gray-500 leading-relaxed max-w-[380px]">
          {sub}
        </div>
      </div>
    </div>
  )
}

function IconSavedYards() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="4" y="6" width="13" height="13" rx="2" stroke="#15803d" strokeWidth="1.6" />
      <rect x="7" y="3" width="13" height="13" rx="2" stroke="#15803d" strokeWidth="1.6" fill="#fff" />
      <path d="M10 11.5l2 2 3.5-4" stroke="#16a34a" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function IconCarePlan() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M5 12a7 7 0 0 1 12-4.95M19 12a7 7 0 0 1-12 4.95"
        stroke="#15803d"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path
        d="M14 5v3h3M10 19v-3H7"
        stroke="#15803d"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="1.6" fill="#16a34a" />
    </svg>
  )
}

function IconBell() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 4a5 5 0 0 1 5 5v3l1.5 3h-13L7 12V9a5 5 0 0 1 5-5z"
        stroke="#15803d"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M10 18a2 2 0 0 0 4 0" stroke="#15803d" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="17" cy="6" r="2" fill="#16a34a" />
    </svg>
  )
}

function LoginLoading() {
  return (
    <div className="w-full max-w-md">
      <div className="bg-white border border-gray-200 rounded-2xl p-12 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-green-600" />
      </div>
    </div>
  )
}

export function LoginContent() {
  return (
    <div className="min-h-screen flex items-center justify-center overflow-x-clip bg-gradient-to-b from-green-50 to-white px-4 py-12">
      <Suspense fallback={<LoginLoading />}>
        <LoginForm />
      </Suspense>
    </div>
  )
}
